import React, { useRef, useState } from 'react';
import { useScrollProgress } from '../../hooks/useScrollProgress';

const assetPath = (file) => `${process.env.PUBLIC_URL}/assets/portfolio/${file}`;

const PROJECTS = [
  {
    name: 'PropertyMart.com.my',
    kind: 'Property marketplace',
    description: 'Property search with advanced filters and virtual tours, on top of listing workflows built to scale.',
    stack: ['React', 'Laravel', 'MySQL', 'Google Cloud'],
    image: assetPath('property-mart.png'),
    alt: 'PropertyMart home page with a property search bar and tabs for new launches, buying and renting',
    device: 'browser',
    stage: 'deep',
    address: 'propertymart.com.my',
    link: { href: 'https://www.propertymart.com.my', label: 'Visit the live site', external: true },
  },
  {
    name: 'Lifestyle Mobile App',
    kind: 'Mobile app',
    description: 'A lifestyle and social app with chat, personalised recommendations and push notifications.',
    stack: ['Flutter', 'Laravel', 'Firebase', 'Google Cloud'],
    image: assetPath('lifestyle-app.png'),
    alt: 'Lifestyle app home screen showing level progress, a streak and daily quests',
    device: 'phone',
    stage: 'cobalt',
    link: { href: '#contact', label: 'Ask for a demo' },
  },
  {
    name: 'E-Commerce Platform',
    kind: 'Commerce and bookings',
    description: 'Payments, inventory automation and merchandising analytics, delivered through a customer app.',
    stack: ['Flutter', 'Laravel', 'Firebase', 'Google Cloud'],
    image: assetPath('ohairpro-app.png'),
    alt: 'Commerce app home screen with weekly highlights and tabs for shop, booking and rewards',
    device: 'phone',
    stage: 'deep',
    link: { href: '#contact', label: 'Ask for a demo' },
  },
];

const isPinnedLayout = () => window.matchMedia('(min-width: 961px)').matches;

const Portfolio = () => {
  const sectionRef = useRef(null);
  const stageRef = useRef(null);
  const [active, setActive] = useState(0);

  useScrollProgress(sectionRef, {
    onChange: (progress) => {
      if (isPinnedLayout()) setActive(Math.min(Math.floor(progress * PROJECTS.length), PROJECTS.length - 1));
    },
  });

  // On phones and tablets the projects are a swipeable row, so sideways scrolling picks the active one.
  const onRowScroll = (event) => {
    if (isPinnedLayout()) return;
    const row = event.currentTarget;
    const progress = row.scrollLeft / Math.max(row.scrollWidth - row.clientWidth, 1);
    setActive(Math.round(progress * (PROJECTS.length - 1)));
  };

  const goTo = (index) => {
    const section = sectionRef.current;
    const travel = section.offsetHeight - window.innerHeight;
    const top = section.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: top + travel * ((index + 0.5) / PROJECTS.length) });
  };

  const onPointerMove = (event) => {
    const rect = stageRef.current.getBoundingClientRect();
    stageRef.current.style.setProperty('--tilt-x', ((event.clientX - rect.left) / rect.width - 0.5).toFixed(3));
    stageRef.current.style.setProperty('--tilt-y', ((event.clientY - rect.top) / rect.height - 0.5).toFixed(3));
  };

  return (
    <section id="portfolio" className="work" ref={sectionRef} aria-labelledby="work-title">
      <div className="work-pin" ref={stageRef} onPointerMove={onPointerMove}>
        <div className="work-index">
          <h2 id="work-title" className="work-title">Shipped work</h2>
          <p className="work-counter" aria-hidden="true">{active + 1} / {PROJECTS.length}</p>
          <ol>
            {PROJECTS.map((project, index) => (
              <li key={project.name}>
                <button
                  type="button"
                  className={index === active ? 'is-active' : ''}
                  aria-current={index === active}
                  onClick={() => goTo(index)}
                >
                  {project.name}
                </button>
              </li>
            ))}
          </ol>
        </div>

        <div className="work-projects" onScroll={onRowScroll}>
          {PROJECTS.map((project, index) => {
            const position = index === active ? 'is-active' : index < active ? 'is-past' : 'is-next';
            return (
              <article key={project.name} className={`project ${position}`}>
                <div className="project-copy">
                  <p className="project-kind">{project.kind}</p>
                  <h3>
                    {project.name.split('.com')[0]}
                    {project.name.includes('.com') && <><wbr />.com{project.name.split('.com')[1]}</>}
                  </h3>
                  <p>{project.description}</p>
                  <ul className="project-stack" aria-label="Technology used">
                    {project.stack.map((tech) => <li key={tech}>{tech}</li>)}
                  </ul>
                  <a
                    href={project.link.href}
                    className="text-link"
                    {...(project.link.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                  >
                    {project.link.label}
                    {project.link.external && (
                      <>
                        <svg className="external-arrow" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.75" aria-hidden="true">
                          <path d="M2 10 10 2M3.5 2H10v6.5" />
                        </svg>
                        <span className="visually-hidden"> (opens in a new tab)</span>
                      </>
                    )}
                  </a>
                </div>
                <div className={`device-stage device-stage--${project.stage}`}>
                  <div className={`device device--${project.device}`}>
                    {project.device === 'browser' && (
                      <div className="device-bar" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                        <em>{project.address}</em>
                      </div>
                    )}
                    <img src={project.image} alt={project.alt} loading="lazy" />
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
